// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    canDisplayFeedbackNonCPS,
    CanDisplayFeedbackWithPolicies,
    FeedbackInitData,
    launchFeedback,
} from '@ms/inapp-feedback-loader';
import {
    FeedbackNotAllowedError,
    IInAppFeedbackInitData,
    IFeedbackLaunchParams,
    getBaseOCVTheme,
    createFeedbackInitOptions,
    InAppFeedbackErrorKind,
    getFeedbackConfigOverrides,
} from './feedback-options';

export interface IFeedbackProvider {
    element: Element;
    initialize: (initData: IInAppFeedbackInitData) => Promise<void>;
    launch: (params: IFeedbackLaunchParams) => Promise<void>;
    // Whether compliance checks passed and feedback can be displayed.
    readonly canDisplay: boolean;
}

export function getFeedbackProvider(element: Element): IFeedbackProvider {
    return new OCVFeedbackProvider(element);
}

class OCVFeedbackProvider implements IFeedbackProvider {
    private initData: IInAppFeedbackInitData | undefined;
    // Holds the updated init options and whether feedback can be displayed.
    private displayPolicy: CanDisplayFeedbackWithPolicies;

    public get canDisplay(): boolean {
        return this.displayPolicy.canDisplay;
    }

    constructor(readonly element: Element) {
        this.displayPolicy = {
            canDisplay: false,
            init: {},
        };
    }

    public async initialize(initData: IInAppFeedbackInitData) {
        this.initData = initData;
        const feedbackInitOptions = createFeedbackInitOptions(this.initData);
        this.displayPolicy = await canDisplayFeedbackNonCPS(feedbackInitOptions);
    }

    public async launch(launchParams: IFeedbackLaunchParams) {
        if (this.initData === undefined) {
            throw new Error('Cannot launch the feedback form because it has not been initialized.');
        } else if (this.displayPolicy.canDisplay !== true) {
            throw new FeedbackNotAllowedError('Cannot launch the feedback form because it is not allowed.');
        }

        // Ensure theme, metadata, and scenario are passed to OCV
        this.displayPolicy.init = {
            ...this.displayPolicy.init,
            themeOptions: {
                baseTheme: getBaseOCVTheme(launchParams.theme),
            },
            feedbackConfig: {
                ...this.displayPolicy.init.feedbackConfig,
                ...getFeedbackConfigOverrides(launchParams.scenario),
                appData: launchParams.metadataJson,
                scenarioConfig: {
                    ...this.displayPolicy.init.feedbackConfig?.scenarioConfig,
                    isScenarioEnabled: true,
                    scenarioType: launchParams.scenario,
                },
            },
            userData: launchParams.userData,
        };

        const feedbackLaunchOptions: FeedbackInitData = {
            ...this.initData,
            inAppFeedbackRef: this.element,
            onInitializationError: (error: Error) => {
                this.initData?.onError?.(error, InAppFeedbackErrorKind.Initialization);
            },
        };
        await launchFeedback(feedbackLaunchOptions, this.displayPolicy.init);
    }
}
